import { useLocalization } from '@lib/localization';
import { Box, Stack, Typography } from '@mui/material';
import { type LocalizedText, type PoiField } from '@store/api/poiTypeEndpoints';
import { LockIcon } from 'react-line-awesome';
import { fallbackTextStyles } from './designer.styles';

/** The language the form is shown in, the one it falls back to, and a label resolved that way. */
export interface Languages {
  locale: string;
  primary: string;
  resolve: (text?: LocalizedText) => string;
}

/** A user-authored label in the chosen language; a missing translation shows as its fallback, faded. */
export const LabelText: React.FC<{ text?: LocalizedText; empty: string; languages: Languages }> = ({
  text, empty, languages,
}) => {
  const shown = languages.resolve(text);

  if (!shown) return <Box component="span" sx={{ color: 'text.disabled' }}>{empty}</Box>;

  return <Box component="span" sx={text?.[languages.locale]?.trim() ? undefined : fallbackTextStyles}>{shown}</Box>;
};

interface Props {
  field: PoiField;
  languages: Languages;
  required: boolean;
  readOnly: boolean;
  /** Something before the label, like Try it's mark for a field an answer brought in. */
  marker?: React.ReactNode;
  /** Something at the end of the row: a toggle's switch, a range's value, the designer's actions. */
  trailing?: React.ReactNode;
}

/**
 * A field's label row, as the device draws it: the label, a required mark, a lock when
 * read-only. Its height is fixed, so whatever appears at its end never moves the form.
 */
const FieldHeading: React.FC<Props> = ({ field, languages, required, readOnly, marker, trailing }) => {
  const { t } = useLocalization();

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, minHeight: 28 }}>
      {marker}
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
        <LabelText text={field.label} empty={t('Customization_NewField')} languages={languages} />
      </Typography>
      {/* Toggle and Range always hold an answer, so a required mark would mean nothing. */}
      {required && field.type !== 'Toggle' && field.type !== 'Range' && (
        <Typography component="span" sx={{ color: 'error.main', fontWeight: 700, lineHeight: 1 }}>*</Typography>
      )}
      {readOnly && <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled', fontSize: 13 }}><LockIcon /></Box>}
      <Box sx={{ flex: 1 }} />
      {trailing}
    </Stack>
  );
};

export default FieldHeading;
